import { collection, addDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from './firebase';
import { Property, PropertyType } from '../types';

// Sample property data to seed the database
const sampleProperties: Omit<Property, 'id'>[] = [
  {
    title: 'Luxury Villa with Ocean View',
    description: 'Stunning luxury villa overlooking the Pacific Ocean in Guanacaste. This property features an infinity pool, outdoor kitchen, and panoramic views.',
    price: 850000,
    location: 'Guanacaste, Costa Rica',
    bedrooms: 4,
    bathrooms: 3.5,
    area: 320,
    type: PropertyType.VILLA,
    images: [
      'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?ixlib=rb-1.2.1&auto=format&fit=crop&w=1567&q=80',
    ],
    features: ['Ocean View', 'Infinity Pool', 'Private Garden', 'Security System'],
    isNew: true,
    isFeatured: true,
    coordinates: { lat: 10.2773, lng: -85.7876 }, // Guanacaste coordinates
    available: true
  },
  {
    title: 'Modern Apartment in San José',
    description: 'Contemporary apartment in the heart of San José with mountain views. Walking distance to restaurants, shops, and amenities.',
    price: 320000,
    location: 'San José, Costa Rica',
    bedrooms: 2,
    bathrooms: 2,
    area: 110,
    type: PropertyType.APARTMENT,
    images: [
      'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80',
    ],
    features: ['City View', 'Parking', 'Gym', 'Pool'],
    coordinates: { lat: 9.9281, lng: -84.0907 }, // San José coordinates
    available: true
  },
  {
    title: 'Beachfront Paradise',
    description: 'Direct beach access property in beautiful Manuel Antonio. Perfect for vacation rentals or your dream home.',
    price: 750000,
    location: 'Manuel Antonio, Costa Rica',
    bedrooms: 3,
    bathrooms: 2,
    area: 200,
    type: PropertyType.HOUSE,
    images: [
      'https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?ixlib=rb-1.2.1&auto=format&fit=crop&w=1050&q=80'
    ],
    features: ['Beachfront', 'Private Terrace', 'Air Conditioning', 'Lush Gardens'],
    isFeatured: true,
    coordinates: { lat: 9.3920, lng: -84.1334 }, // Manuel Antonio coordinates
    available: true
  },
  {
    title: 'Development Land in Montezuma',
    description: 'Prime development land with incredible views and development permits in place. Perfect for resort or luxury home development.',
    price: 420000,
    location: 'Montezuma, Costa Rica',
    bedrooms: 0,
    bathrooms: 0,
    area: 5000,
    type: PropertyType.LAND,
    images: [
      'https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-1.2.1&auto=format&fit=crop&w=1189&q=80'
    ],
    features: ['Ocean View', 'Development Permits', 'Utilities Available', 'Road Access'],
    coordinates: { lat: 9.6566, lng: -85.0678 }, // Montezuma coordinates
    available: true
  },
  {
    title: 'Eco-Friendly Jungle Retreat',
    description: 'Sustainable home nestled in the jungle canopy with wildlife all around. Off-grid capabilities with modern amenities.',
    price: 395000,
    location: 'Uvita, Costa Rica',
    bedrooms: 2,
    bathrooms: 2,
    area: 150,
    type: PropertyType.HOUSE,
    images: [
      'https://images.unsplash.com/photo-1518780664697-55e3ad937233?ixlib=rb-1.2.1&auto=format&fit=crop&w=1001&q=80'
    ],
    features: ['Solar Power', 'Rainwater Collection', 'Organic Garden', 'Nature Trails'],
    isNew: true,
    coordinates: { lat: 9.1640, lng: -83.7349 }, // Uvita coordinates
    available: true
  },
  {
    title: 'Commercial Space in Tamarindo',
    description: 'Prime commercial property in the bustling tourist area of Tamarindo. Ideal for restaurant or retail business.',
    price: 550000,
    location: 'Tamarindo, Costa Rica',
    bedrooms: 0,
    bathrooms: 2,
    area: 200,
    type: PropertyType.COMMERCIAL,
    images: [
      'https://images.unsplash.com/photo-1497366811353-6870744d04b2?ixlib=rb-1.2.1&auto=format&fit=crop&w=1050&q=80'
    ],
    features: ['High Foot Traffic', 'Tourist Area', 'Parking', 'Existing Business'],
    coordinates: { lat: 10.2993, lng: -85.8371 }, // Tamarindo coordinates
    available: true
  }
];

// Check if properties already exist to avoid duplicates
const checkIfPropertiesExist = async (): Promise<boolean> => {
  try {
    const propertiesCollection = collection(db, 'properties');
    const querySnapshot = await getDocs(query(
      propertiesCollection, 
      where('title', '==', sampleProperties[0].title)
    ));
    
    return !querySnapshot.empty;
  } catch (error) {
    console.error('Error checking if properties exist:', error);
    return false;
  }
};

// Add sample properties to the database
export const seedDatabase = async (): Promise<boolean> => {
  try {
    // Check if properties already exist
    const propertiesExist = await checkIfPropertiesExist();
    
    if (propertiesExist) {
      console.log('Sample properties already exist in the database. Skipping seeding.');
      return false;
    }
    
    console.log('Starting to seed database with sample properties...');
    const propertiesCollection = collection(db, 'properties');
    
    for (const property of sampleProperties) {
      await addDoc(propertiesCollection, property);
    }
    
    console.log('Successfully added all sample properties to the database!');
    return true;
  } catch (error) {
    console.error('Error seeding database:', error);
    throw error;
  }
};